import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/ui';
import { queryClient } from './lib/queryClient';
import './index.css';

// Handle dynamic import / chunk errors globally before they crash the UI
const RELOAD_KEY = 'z3_chunk_reload';
const THROTTLE_MS = 15_000;

function handleStaleChunkError(e?: Event | PromiseRejectionEvent, reason?: unknown) {
  const msg = (reason as Error)?.message || String(reason || '');
  const isChunkError =
    !reason ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Failed to load module script');

  if (isChunkError) {
    if (e?.preventDefault) e.preventDefault();
    const lastReload = sessionStorage.getItem(RELOAD_KEY);
    const now = Date.now();
    if (!lastReload || now - Number(lastReload) > THROTTLE_MS) {
      sessionStorage.setItem(RELOAD_KEY, String(now));
      console.warn('New deployment chunk mismatch detected. Reloading app...', msg);
      window.location.reload();
    }
  }
}

// Vite emits 'vite:preloadError' when an import() fails
window.addEventListener('vite:preloadError', (event) => {
  handleStaleChunkError(event);
});

// Catches unhandled promise rejections from module imports
window.addEventListener('unhandledrejection', (event) => {
  handleStaleChunkError(event, event.reason);
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <App />
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => {}));
}

/**
 * Warm the two busiest staff screens (shop billing and the parking gate) once the page is idle, so the
 * first tap after login opens them without a download. Only in the staff area: the customer app never
 * needs them, and people on a metered connection with data saver on are left alone. The imports
 * resolve to the same chunks App.tsx lazy-loads, so nothing is downloaded twice.
 */
function prefetchStaffScreens() {
  if (!location.pathname.startsWith('/admin')) return;
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  if (connection?.saveData) return;
  const warm = () => {
    import('./pages/staff/shop/Pos').catch(() => {});
    import('./pages/staff/Gate').catch(() => {});
  };
  if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 4000 });
  else setTimeout(warm, 2000);
}
if (import.meta.env.PROD) window.addEventListener('load', prefetchStaffScreens);
